import type { FC } from 'react';
import React from 'react';
import type { QueryResult } from '@apollo/react-common';

import { shouldShowMobileWeb } from '@confluence/mobile-detection';
import { useSessionData } from '@confluence/session-data';

import { ViewPageExternal } from '../ViewPageExternal';
import type { SharedContentQuery as SharedContentQueryType } from '../__types__/SharedContentQuery';
import type { PublicLinkInformationQuery as PublicLinkInformationQueryType } from '../__types__/PublicLinkInformationQuery';
import { useWithUserTypes } from '../hooks/useWithUserTypes';
import { WhiteboardExternal } from '../whiteboard/WhiteboardExternal';
import type { ContentRenderer } from '../ViewPageExternal';

type Props = {
	contentId: string;
	shareToken: string;
	sharedContentQuery: QueryResult<SharedContentQueryType, any>;
	/** Optional query hash for jumping to a heading. Example: `#Some-Heading` */
	queryHash?: string;
	/**
	 * Content renderer React component. The `@confluence/external-share` package cannot import the
	 * existing fabric renderer (because it's in a classic package) so it is passed in as a prop.
	 */
	ContentRenderer: ContentRenderer;
	isSpaceAliasFFEnabled: boolean;
	isUsingPublicLinkInfo: boolean;
	publicLinkInformationQuery: QueryResult<PublicLinkInformationQueryType, any>;
};

export const ExternalContent: FC<Props> = (props) => {
	const {
		contentId,
		shareToken,
		queryHash,
		sharedContentQuery,
		ContentRenderer,
		isSpaceAliasFFEnabled,
		isUsingPublicLinkInfo,
		publicLinkInformationQuery,
	} = props;

	const content = isUsingPublicLinkInfo
		? publicLinkInformationQuery?.data?.publicLinkInformation
		: sharedContentQuery?.data?.singleContent;
	const isMobileView = shouldShowMobileWeb();
	const { userType } = useWithUserTypes();
	const { cloudId } = useSessionData();

	if (!content || !content.space) {
		return null;
	}

	const spaceKey = content.space?.key || '';
	const spaceAlias = (isSpaceAliasFFEnabled && content.space?.alias) || spaceKey;

	return (
		<>
			{content.type === 'whiteboard' && (
				<WhiteboardExternal
					contentId={contentId}
					shareToken={shareToken}
					sharedContentQuery={sharedContentQuery}
					isSpaceAliasFFEnabled={isSpaceAliasFFEnabled}
				/>
			)}
			{content.type === 'page' && (
				<ViewPageExternal
					contentId={contentId}
					queryHash={queryHash}
					isMobileView={isMobileView}
					ContentRenderer={ContentRenderer}
					spaceKey={spaceAlias}
					userType={userType}
					cloudId={cloudId}
					isUsingPublicLinkInfo={isUsingPublicLinkInfo}
				/>
			)}
		</>
	);
};
